import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { absolute, DEFAULT_OG_IMAGE, metaDescription, pageTitle, SITE_NAME, SITE_URL, type SeoInput } from '@/lib/seo';

/**
 * Sets the page's title, description, canonical and share tags.
 *
 * Imperative rather than React 19's `<title>`/`<meta>` hoisting, because the
 * hoisting appends: `index.html` already carries a default of each, and the
 * Vercel function injects the real ones server-side, so a second copy would
 * leave two descriptions on the page and let a crawler pick either.
 */

/** Creates the tag on first use, then keeps reusing it. */
function tag(selector: string, create: () => HTMLElement): HTMLElement {
    const existing = document.head.querySelector<HTMLElement>(selector);
    if (existing) return existing;
    const made = create();
    document.head.appendChild(made);
    return made;
}

const named = (name: string, content: string): void => {
    const el = tag(`meta[name="${name}"]`, () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', name);
        return meta;
    });
    el.setAttribute('content', content);
};

const property = (name: string, content: string): void => {
    const el = tag(`meta[property="${name}"]`, () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', name);
        return meta;
    });
    el.setAttribute('content', content);
};

const remove = (selector: string): void => document.head.querySelector(selector)?.remove();

export default function Seo({
    title,
    description,
    canonical,
    image,
    imageAlt,
    type = 'website',
    publishedTime,
    author,
    tags,
    noindex,
    bare,
}: SeoInput) {
    const { pathname } = useLocation();

    useEffect(() => {
        const fullTitle = pageTitle(title, bare);
        const summary = metaDescription(description);
        const url = absolute(canonical ?? pathname);
        const picture = absolute(image || DEFAULT_OG_IMAGE);

        document.title = fullTitle;
        named('description', summary);
        named('robots', noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1');

        const link = tag('link[rel="canonical"]', () => {
            const el = document.createElement('link');
            el.setAttribute('rel', 'canonical');
            return el;
        });
        link.setAttribute('href', url);

        property('og:title', fullTitle);
        property('og:description', summary);
        property('og:url', url);
        property('og:type', type);
        property('og:site_name', SITE_NAME);
        property('og:image', picture);
        property('og:image:alt', imageAlt || fullTitle);
        named('twitter:card', 'summary_large_image');
        named('twitter:title', fullTitle);
        named('twitter:description', summary);
        named('twitter:image', picture);

        // Article-only tags, cleared again on a page that is not one.
        if (type === 'article') {
            if (publishedTime) property('article:published_time', publishedTime);
            else remove('meta[property="article:published_time"]');
            if (author) property('article:author', author);
            else remove('meta[property="article:author"]');
            remove('meta[property="article:tag"]');
            for (const value of (tags ?? []).slice(0, 6)) {
                const meta = document.createElement('meta');
                meta.setAttribute('property', 'article:tag');
                meta.setAttribute('content', value);
                document.head.appendChild(meta);
            }
        } else {
            remove('meta[property="article:published_time"]');
            remove('meta[property="article:author"]');
            for (const el of document.head.querySelectorAll('meta[property="article:tag"]')) el.remove();
        }
    }, [title, description, canonical, image, imageAlt, type, publishedTime, author, tags, noindex, bare, pathname]);

    return null;
}

export { SITE_URL };
